/**
 * The chart-tap sheet and the note sheet. Tapping open water gives the tapped
 * point's position, its range and bearing measured from the boat, and the
 * things we can do from there: make a waypoint, start a route, or drop a note.
 *
 * Long-press on the chart drops a waypoint directly (handled in MapView), which
 * is why there is no waypoint button cluttering the resting screen.
 */
import { useEffect, useState } from 'react';
import { useChartPlotterStore } from '@/store';
import { useShellStore } from '@/store/shell';
import { useRoutingStore, nextWaypointId } from '@/store/routing';
import { useNotesStore, nextNoteId } from '@/store/notes';
import { setDestinationAndOfferRoute } from './setDestinationFlow';
import { usePassagePlanStore } from '@/store/passagePlan';
import { useRoutePlanStore } from '@/store/routePlan';
import { formatLatLon, formatBearing, formatDistance, rhumbBearingDeg, rhumbDistanceNm } from '@/nav';
import { zocAccuracyText } from '@/chart';
import { haptic } from '@/ui/haptics';
import { Sheet } from './Sheet';
import { useEnvStore } from '@/store/environment';
import { getHeightPort } from '@/tides/heights';
import { chartedDepthDetailAtNow, chartedZocAtNow } from '@/map/chartQuery';
import { providerHasDepthData } from '@/chart-sources';
import { useUnitsStore, toDepth, depthUnitLabel, type DepthUnit } from '@/store/units';
import type { ChartedDepthDetail } from '@/safety';
import { trainingTideCovers } from '@/chart-sources';

export function ChartTapSheet() {
  const chartTap = useShellStore((s) => s.chartTap);
  const chartTapZoc = useShellStore((s) => s.chartTapZoc);
  const openSheet = useShellStore((s) => s.openSheet);
  const closeSheet = useShellStore((s) => s.closeSheet);
  const showToast = useShellStore((s) => s.showToast);
  const setEditingWaypoint = useShellStore((s) => s.setEditingWaypoint);
  const ownship = useChartPlotterStore((s) => s.ownship);
  const addMark = useRoutingStore((s) => s.addMark);
  const dropRouteWaypoint = useRoutingStore((s) => s.dropRouteWaypoint);
  const planActive = usePassagePlanStore((s) => s.status !== 'idle');
  const setStartPoint = useRoutePlanStore((s) => s.setStartPoint);
  const addVia = useRoutePlanStore((s) => s.addVia);
  const marks = useRoutingStore((s) => s.marks);
  const addNote = useNotesStore((s) => s.addNote);
  const setEditingNote = useShellStore((s) => s.setEditingNote);

  const lat = chartTap ? formatLatLon(chartTap[1], 'lat') : '—';
  const lon = chartTap ? formatLatLon(chartTap[0], 'lon') : '—';
  const hasBoat = !!ownship.position && !!chartTap;
  const rng = hasBoat ? rhumbDistanceNm(ownship.position!, chartTap!) : null;
  const brg = hasBoat ? rhumbBearingDeg(ownship.position!, chartTap!) : null;

  function makeMark() {
    if (!chartTap) return;
    // Drop the mark, then open its editor so we're asked for the icon, name,
    // notes and position rather than getting a default square with no say.
    const id = nextWaypointId();
    addMark({ id, position: chartTap, name: `M${marks.length + 1}`, icon: 'plot-waypoint' });
    haptic('commit');
    setEditingWaypoint({ type: 'mark', markId: id });
    openSheet('waypoint');
  }

  function startRoute() {
    if (!chartTap) return;
    // First waypoint of a new route; this enters build mode (long-press adds more).
    dropRouteWaypoint(chartTap);
    closeSheet();
    showToast('Long-press the chart to add waypoints');
  }

  function makeStart() {
    if (!chartTap) return;
    // The passage start point, when planning a route from somewhere other than the boat.
    setStartPoint(chartTap);
    closeSheet();
    showToast('Start point set');
  }

  function makeDestination() {
    if (!chartTap) return;
    // The passage destination: where a planned route is made for. Marked with its
    // own destination-anchor symbol, distinct from a waypoint or a charted anchorage.
    // Setting it also offers, straight away, to plot a route to it.
    haptic('commit');
    closeSheet();
    setDestinationAndOfferRoute(chartTap);
  }

  function goVia() {
    if (!chartTap) return;
    // A must-pass point the route threads through (no stop). Replot in the Route step.
    addVia(chartTap, 0);
    closeSheet();
    showToast('Via point added — replot in the Route step to thread through it');
  }

  function stopHere() {
    if (!chartTap) return;
    // A timed stopover: a 1 h default the Route step lets you adjust. The dwell
    // shifts the downstream ETAs once the route's replotted.
    addVia(chartTap, 1);
    closeSheet();
    showToast('Stop added (1 h) — set how long, and replot, in the Route step');
  }

  function makeNote() {
    if (!chartTap) return;
    // We already have a location here (the tapped point), so drop the note
    // straight away and open its editor — no need for the tap-to-place step.
    const id = nextNoteId();
    const now = Date.now();
    addNote({ id, position: chartTap, text: '', createdMs: now, updatedMs: now });
    haptic('commit');
    setEditingNote(id);
    openSheet('note');
  }

  return (
    <Sheet
      name="chart"
      title="Chart position"
      sub="The point we tapped, with its range and bearing measured from the boat."
    >
      <div className="acp-kv">
        <span className="k">Latitude</span>
        <span className="v">{lat}</span>
      </div>
      <div className="acp-kv">
        <span className="k">Longitude</span>
        <span className="v">{lon}</span>
      </div>
      <div className="acp-kv">
        <span className="k">Range from boat</span>
        <span className="v">{rng !== null ? formatDistance(rng) : 'No fix'}</span>
      </div>
      <div className="acp-kv">
        <span className="k">Bearing from boat</span>
        <span className="v">{brg !== null ? formatBearing(brg) : 'No fix'}</span>
      </div>
      {zocAccuracyText(chartTapZoc) && (
        <p className="sub" style={{ marginTop: 8 }}>
          {zocAccuracyText(chartTapZoc)}
        </p>
      )}
      <div className="acp-btn-row">
        <button type="button" className="acp-btn primary" onClick={makeDestination}>
          Set as destination
        </button>
        {planActive && (
          <button type="button" className="acp-btn" onClick={makeStart}>
            Set as start
          </button>
        )}
        {planActive && (
          <button type="button" className="acp-btn" onClick={goVia}>
            Go via here
          </button>
        )}
        {planActive && (
          <button type="button" className="acp-btn" onClick={stopHere}>
            Stop here
          </button>
        )}
        <button type="button" className="acp-btn" onClick={makeMark}>
          Single waypoint
        </button>
        <button type="button" className="acp-btn" onClick={startRoute}>
          Start route
        </button>
        <button type="button" className="acp-btn full" onClick={makeNote}>
          Add a note
        </button>
      </div>
    </Sheet>
  );
}

export function NoteSheet() {
  const editingNoteId = useShellStore((s) => s.editingNoteId);
  const setEditingNote = useShellStore((s) => s.setEditingNote);
  const closeSheet = useShellStore((s) => s.closeSheet);
  const showToast = useShellStore((s) => s.showToast);
  const notes = useNotesStore((s) => s.notes);
  const updateNote = useNotesStore((s) => s.updateNote);
  const removeNote = useNotesStore((s) => s.removeNote);
  const note = editingNoteId ? (notes.find((n) => n.id === editingNoteId) ?? null) : null;

  const [title, setTitle] = useState('');
  const [text, setText] = useState('');

  // Load the open note's current title/text into the fields whenever we switch
  // to a different note (or open a freshly placed, still-empty one). Keyed on the
  // id, so typing doesn't get stomped by this on every keystroke.
  useEffect(() => {
    setTitle(note?.title ?? '');
    setText(note?.text ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingNoteId]);

  function done() {
    setEditingNote(null);
    closeSheet();
  }

  function save() {
    if (!editingNoteId) {
      done();
      return;
    }
    const t = title.trim();
    const body = text.trim();
    // The note is already pinned on the chart from the moment it was placed, so a
    // Save keeps it even with nothing typed in yet: an untitled post-it is still a
    // valid "come back to this spot" pin, and it draws as the sticky-note icon
    // with no label. Removing it here silently deleted the note the skipper had
    // just dropped, which read as "I made a note but I can't see it on the chart"
    // (fb-ms1npc4g-3). It was also inconsistent with Cancel, which keeps the same
    // empty note. To get rid of a note we use Delete; Save and Cancel both keep it.
    updateNote(editingNoteId, { title: t || undefined, text: body });
    setEditingNote(null);
    closeSheet();
    showToast('Note saved');
  }

  function del() {
    if (editingNoteId) removeNote(editingNoteId);
    setEditingNote(null);
    closeSheet();
    showToast('Note deleted');
  }

  return (
    <Sheet
      name="note"
      title="Note"
      sub="A post-it pinned on the chart. Give it a short title for the label and write what we want to remember here. Handy for a hazard we have spotted or a spot to come back to."
    >
      <input
        type="text"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        className="acp-note-title"
        placeholder="Title (shown on the chart), e.g. Pot buoys"
      />
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        className="acp-note-input"
        rows={3}
        placeholder="For example: pot buoys here at low water"
      />
      <div className="acp-btn-row">
        <button type="button" className="acp-btn" onClick={done}>
          Cancel
        </button>
        {note && (
          <button type="button" className="acp-btn" onClick={del} aria-label="Delete note">
            Delete
          </button>
        )}
        <button type="button" className="acp-btn primary" onClick={save}>
          Save note
        </button>
      </div>
    </Sheet>
  );
}


/**
 * Put the charted depth at a point into words the chart can stand behind.
 *
 * Soundings are a measured depth at a spot, so where one sits close by it leads.
 * A depth area is a band, so it is given as the band it is ("0 to 18 m") rather
 * than as its shallow end, which read on its own says there is no water here.
 * Returns null when the chart carries neither, so the caller can say so.
 */
function describeChartedDepth(d: ChartedDepthDetail | null, unit: DepthUnit): string | null {
  if (!d) return null;
  const u = depthUnitLabel(unit);
  const n = (m: number): string => toDepth(m, unit).toFixed(1);

  let area: string | null = null;
  if (d.areaMinM != null && d.areaMaxM != null) area = `${n(d.areaMinM)} to ${n(d.areaMaxM)} ${u}`;
  else if (d.areaMinM != null) area = `${n(d.areaMinM)} ${u} or deeper`;
  else if (d.areaMaxM != null) area = `shallower than ${n(d.areaMaxM)} ${u}`;

  if (d.soundingM != null) {
    const sounding = `${n(d.soundingM)} ${u} sounding`;
    return area ? `${sounding}, in a depth area of ${area}` : sounding;
  }
  return area ? `Depth area, ${area}` : null;
}

export function WhatsHereSheet() {
  const chartTap = useShellStore((s) => s.chartTap);
  const ownship = useChartPlotterStore((s) => s.ownship);
  const heightPortId = useEnvStore((s) => s.heightPortId);
  const chartProviderId = useChartPlotterStore((s) => s.chartProviderId);
  const units = useUnitsStore();

  const lat = chartTap ? formatLatLon(chartTap[1], 'lat') : '\u2014';
  const lon = chartTap ? formatLatLon(chartTap[0], 'lon') : '\u2014';
  const hasBoat = !!ownship.position && !!chartTap;
  const rng = hasBoat ? rhumbDistanceNm(ownship.position!, chartTap!) : null;
  const brg = hasBoat ? rhumbBearingDeg(ownship.position!, chartTap!) : null;

  // Depth only lives in the vector ENC. On a raster/tiles source there is
  // nothing to read, so we say so rather than imply a reading we haven't got.
  // Three honest states: a value, "nothing charted right here", or "no depth on
  // this source".
  const sourceHasDepth = providerHasDepthData(chartProviderId);
  // A depth area carries a RANGE, not a depth, so printing its shallow end as a
  // single figure says "0.0 m" over water the chart sounds at 12 and 15. We read
  // the parts instead and say which is which, so the number on screen is one the
  // chart actually claims.
  const depth = chartTap && sourceHasDepth ? chartedDepthDetailAtNow(chartTap) : null;
  const depthText = !sourceHasDepth
    ? 'No depth on this chart source'
    : (describeChartedDepth(depth, units.depth) ?? 'Nothing charted right here');

  // Chart accuracy (CATZOC/ZOC) rides in the same vector ENC M_QUAL polygons as
  // depth, so it reads from the same live-cell bridge. Same three honest states:
  // a plain-English accuracy sentence, "nothing charted right here", or "no
  // quality on this source" when we're on a raster/tile chart.
  const zocBandHere = chartTap && sourceHasDepth ? chartedZocAtNow(chartTap) : null;
  const accuracyText = !sourceHasDepth
    ? 'No accuracy data on this chart source'
    : zocBandHere
      ? (zocAccuracyText(zocBandHere) ?? 'Chart accuracy here has not been assessed')
      : 'No accuracy charted right here';

  // The reference port heights are read from here. Primary training ports carry
  // no coordinates, so this is the active reference port rather than a computed
  // geographic nearest - honest about what it is.
  // Only name a reference port when the training model actually covers this water.
  // On a real chart the Namley ports are fictional, and naming one here invites a
  // skipper to think we have tidal data for where they are pressing. Saying we
  // have none is the honest answer, and it is the one the UKHO screenshots had to
  // explain away in a caption.
  const tidePort = trainingTideCovers(chartProviderId) ? getHeightPort(heightPortId) : null;

  return (
    <Sheet
      name="whatshere"
      title="What's here?"
      sub="A quick read of the spot we pressed: where it is, what's charted under it, and the tide port we read heights from here."
    >
      <div className="acp-kv">
        <span className="k">Latitude</span>
        <span className="v">{lat}</span>
      </div>
      <div className="acp-kv">
        <span className="k">Longitude</span>
        <span className="v">{lon}</span>
      </div>
      <div className="acp-kv">
        <span className="k">Charted depth</span>
        <span className="v">{depthText}</span>
      </div>
      <div className="acp-kv">
        <span className="k">Chart accuracy</span>
        <span className="v">{accuracyText}</span>
      </div>
      <div className="acp-kv">
        <span className="k">Range from boat</span>
        <span className="v">{rng !== null ? formatDistance(rng) : 'No fix'}</span>
      </div>
      <div className="acp-kv">
        <span className="k">Bearing from boat</span>
        <span className="v">{brg !== null ? formatBearing(brg) : 'No fix'}</span>
      </div>
      <div className="acp-kv">
        <span className="k">Tide port (reference)</span>
        <span className="v">{tidePort ? tidePort.name : 'No tidal data for this area'}</span>
      </div>
    </Sheet>
  );
}
